import type { Locale } from './locale';

/**
 * The API speaks UTC instants and Muscat business dates (YYYY-MM-DD).
 * Everything shown to people is in Asia/Muscat, which is UTC+4 all year
 * (Oman has no daylight saving time).
 */
export const MUSCAT_TZ = 'Asia/Muscat';
const MUSCAT_OFFSET_MIN = 4 * 60;

const DAYS_AR = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'] as const;
const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const MONTHS_AR = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
] as const;
const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** A business calendar date, "2026-10-08". */
export type IsoDate = string;
/** A UTC instant as the API sends it, "2026-10-08T06:00:00Z". */
export type IsoInstant = string;

export interface MuscatParts {
  date: IsoDate;
  year: number;
  month: number; // 1–12
  day: number;
  hour: number; // 0–23
  minute: number;
  weekday: number; // 0 = Sunday
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toDate(v: Date | IsoInstant | number): Date {
  return v instanceof Date ? v : new Date(v);
}

/** Wall-clock parts in Muscat for an instant. */
export function muscatParts(instant: Date | IsoInstant | number): MuscatParts {
  const ms = toDate(instant).getTime() + MUSCAT_OFFSET_MIN * 60_000;
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return {
    date: `${year}-${pad(month)}-${pad(day)}`,
    year,
    month,
    day,
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    weekday: d.getUTCDay(),
  };
}

/** The Muscat business date of an instant. */
export function muscatDate(instant: Date | IsoInstant | number): IsoDate {
  return muscatParts(instant).date;
}

/** Today's date in Muscat. */
export function muscatToday(now: Date = new Date()): IsoDate {
  return muscatDate(now);
}

/** The UTC instant of a Muscat wall-clock time: ("2026-10-08", 10) → "2026-10-08T06:00:00.000Z". */
export function muscatInstant(date: IsoDate, hour: number, minute = 0): IsoInstant {
  const [y, m, d] = parseDate(date);
  return new Date(Date.UTC(y, m - 1, d, hour, minute) - MUSCAT_OFFSET_MIN * 60_000).toISOString();
}

export function parseDate(date: IsoDate): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new RangeError(`not a date: ${date}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function dateUtc(date: IsoDate): Date {
  const [y, m, d] = parseDate(date);
  return new Date(Date.UTC(y, m - 1, d));
}

function isoOf(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, n: number): IsoDate {
  const d = dateUtc(date);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
}

/** Whole days from a to b ("2026-10-04" → "2026-10-09" = 5). */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((dateUtc(b).getTime() - dateUtc(a).getTime()) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: IsoDate): number {
  return dateUtc(date).getUTCDay();
}

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

export function dayName(locale: Locale, weekday: number): string {
  return (locale === 'ar' ? DAYS_AR : DAYS_EN)[((weekday % 7) + 7) % 7] ?? '';
}

export function monthName(locale: Locale, month: number): string {
  return (locale === 'ar' ? MONTHS_AR : MONTHS_EN)[(((month - 1) % 12) + 12) % 12] ?? '';
}

/** "الخميس 8 أكتوبر" / "Thursday 8 October" (the prototype's dLong). */
export function formatDateLong(locale: Locale, date: IsoDate): string {
  const [, m, d] = parseDate(date);
  return `${dayName(locale, weekdayOf(date))} ${d} ${monthName(locale, m)}`;
}

/** "8 أكتوبر" / "8 October" (the prototype's dShort). */
export function formatDateShort(locale: Locale, date: IsoDate): string {
  const [, m, d] = parseDate(date);
  return `${d} ${monthName(locale, m)}`;
}

/** "8 أكتوبر 2026" / "8 October 2026". */
export function formatDateFull(locale: Locale, date: IsoDate): string {
  const [y, m, d] = parseDate(date);
  return `${d} ${monthName(locale, m)} ${y}`;
}

/** "أكتوبر 2026" / "October 2026" for "2026-10". */
export function formatMonth(locale: Locale, month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${monthName(locale, m)} ${y}`;
}

export function meridiem(locale: Locale, hour: number): string {
  const am = ((hour % 24) + 24) % 24 < 12;
  return locale === 'ar' ? (am ? 'ص' : 'م') : am ? 'AM' : 'PM';
}

/** 12-hour clock like the prototype: (10) → "10:00 ص", (22, 30) → "10:30 م" / "10:30 PM". */
export function formatHour(locale: Locale, hour: number, minute = 0): string {
  const h = ((hour % 24) + 24) % 24;
  return `${h % 12 || 12}:${pad(minute)} ${meridiem(locale, h)}`;
}

/** The Muscat time of an instant: "10:00 ص". */
export function formatTime(locale: Locale, instant: Date | IsoInstant | number): string {
  const p = muscatParts(instant);
  return formatHour(locale, p.hour, p.minute);
}

/** "8 أكتوبر · 10:12 ص" (the prototype's activity log stamp). */
export function formatDateTime(locale: Locale, instant: Date | IsoInstant | number): string {
  const p = muscatParts(instant);
  return `${formatDateShort(locale, p.date)} · ${formatHour(locale, p.hour, p.minute)}`;
}

/** "الخميس 8 أكتوبر · 10:00 ص". */
export function formatDateTimeLong(locale: Locale, instant: Date | IsoInstant | number): string {
  const p = muscatParts(instant);
  return `${formatDateLong(locale, p.date)} · ${formatHour(locale, p.hour, p.minute)}`;
}

/** Hours between two instants, rounded to minutes. */
export function hoursBetween(a: Date | IsoInstant, b: Date | IsoInstant): number {
  return Math.round((toDate(b).getTime() - toDate(a).getTime()) / 60_000) / 60;
}
